import _ from 'underscore';
import Promise from 'bluebird';
import { Deserializer } from './deserializer';
import type { FilterObject, FilterStorageOptions, PreferenceRecord, PreferenceStore } from './types';

function getPreferenceFilter(name: string, preference: PreferenceStore, preferenceQuery: Record<string, string>) {
  return _.first((preference.filter?.({
    where: _.defaults({ name }, preferenceQuery),
  }) || []) as PreferenceRecord[]);
}

function reject(actionName: string) {
  return () => Promise.reject({ // eslint-disable-line prefer-promise-reject-errors
    error: `FilterPreference${actionName}`,
  });
}

export class SavedFilterStorage {
  deserializer: Deserializer;
  maxSavedFilterLimit: number;
  preference: PreferenceStore;
  preferenceInitPromise: Promise<any> | any;
  preferenceQuery: Record<string, string>;

  /**
   *
   * @param {boolean} getLegacySaved - Whether to save filter through UI server
   * to legacy filter storage
   * @param {string} featureName - Name of feature used to construct resource key
   * @param {Object} filterEntityOptions - Options to create FilterEntity,
   * contains entity schema
   * @param {function} preferenceStoreFactory - A method to create an instance
   * of component/bingads-grid/service/grid-preferences
   * @param {Number} maxSavedFilterLimit - Limit to impose on the number of saved
   * filters (10 by default)
   */
  constructor({
    getLegacySaved = true,
    featureName = '',
    filterEntityOptions = {},
    preferenceStoreFactory,
    maxSavedFilterLimit = 10,
    parseIdAsInt,
  }: FilterStorageOptions = {}) {
    if (!preferenceStoreFactory) {
      throw new Error('No PreferenceStore factory is provided');
    }
    this.preference = preferenceStoreFactory({
      getLegacySaved,
      gridName: featureName,
      entitySchema: filterEntityOptions.entitySchema,
    });
    this.preferenceQuery = {
      project: 'CampaignUI',
      grid: featureName,
    };
    this.preferenceInitPromise = this.preference.findAll?.({ where: this.preferenceQuery });
    this.maxSavedFilterLimit = maxSavedFilterLimit;
    this.deserializer = new Deserializer({ filterEntityOptions, parseIdAsInt });
  }

  /**
   * @param {Object} query - Filter query object
   * @param {string} name - Filter name
   * @return {Object} A promise that resolve to object with id of saved filter
   */
  save({
    query,
    name,
  }: FilterObject) {
    const exist = getPreferenceFilter(name, this.preference, this.preferenceQuery);

    if (exist) {
      return this.preference.update(exist.id, { query })
        .then((result: PreferenceRecord) => ({
          id: result.id,
        }), reject('Update'));
    }

    return this.preference.create(_.defaults({
      query,
      name,
    }, this.preferenceQuery))
      .then((result: PreferenceRecord) => ({
        id: result.id,
      }), reject('Create'));
  }

  /**
   * @return {Object} A promise that resolve to object with id, name and query of saved filter
   */
  get() {
    return this.preferenceInitPromise
      .then(() => {
        let filters = (this.preference.filter?.({ where: this.preferenceQuery }) || []) as PreferenceRecord[];

        filters = _.map(
          filters,
          _.bind(this.deserializer.deserialize, this.deserializer)
        );

        filters = _.reject(filters, filter => _.isEmpty(filter.query));

        return filters;
      }, reject('Get'));
  }

  /**
   * @return {Object} A promise that resolve to object with id of deleted filter
   */
  delete({
    name,
  }: FilterObject) {
    const exist = getPreferenceFilter(name, this.preference, this.preferenceQuery);

    if (exist) {
      return this.preference.destroy(exist.id).then((id: string | number) => ({
        id,
      }), reject('Delete'));
    }

    return Promise.reject({ // eslint-disable-line prefer-promise-reject-errors
      error: 'FilterPreferenceNotExist',
    });
  }
}
